import React, { useEffect } from 'react';
import type { FormControl, FormEffectHandler } from '@atlas-forms/types-js';
import { getGlobalReactRegistry } from '../controls/ReactControlRegistry';

interface FormScopeProcessorProps {
  /** All controls with scope === 'form' extracted from the schema */
  controls: FormControl[];
}

/**
 * Runs the formEffect handler registered for each form-scope control.
 * Cleanup functions are called when controls change or component unmounts.
 * Renders no DOM output.
 */
export const FormScopeProcessor: React.FC<FormScopeProcessorProps> = ({ controls }) => {
  useEffect(() => {
    if (controls.length === 0) return;

    // Registry is a stable singleton — plugins are registered once at app start and
    // never replaced at runtime. It is intentionally excluded from the dependency array:
    // adding it would require the registry to be a React-observable value (it is not),
    // and would cause spurious re-runs. If a plugin is registered after this effect fires,
    // the next controls change (e.g. schema reload) will naturally pick it up.
    const registry = getGlobalReactRegistry();
    const cleanups: Array<() => void> = [];

    for (const control of controls) {
      const plugin = registry.get(control.type);
      const effect = plugin?.formEffect as FormEffectHandler | undefined;
      if (effect) {
        const cleanup = effect(control.config ?? {});
        if (typeof cleanup === 'function') {
          cleanups.push(cleanup);
        }
      }
    }

    return () => {
      for (const cleanup of cleanups) {
        cleanup();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controls]);

  return null;
};
